// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useCallback } from 'react';
import Modal from 'antd/lib/modal';
import { LoadingOutlined } from '@ant-design/icons';
import { DimensionType, CVATCore } from 'cvat-core-wrapper';
import Menu, { MenuInfo } from 'components/dropdown-menu';
import { usePlugins } from 'utils/hooks';
import { CombinedState } from 'reducers';
import { t } from 'utils/i18n';

type AnnotationFormats = Awaited<ReturnType<CVATCore['server']['formats']>>;

interface Props {
    referenceID: number;
    projectID: number | null;
    referenceMode: string;
    bugTracker: string;
    loaders: AnnotationFormats['loaders'];
    dumpers: AnnotationFormats['dumpers'];
    inferenceIsActive: boolean;
    referenceDimension: DimensionType;
    backupIsActive: boolean;
    onClickMenu: (params: MenuInfo) => void;
}


export enum Actions {
    LOAD_REFERENCE_ANNO = 'load_reference_anno_ref',
    EXPORT_REFERENCE_DATASET = 'export_reference_dataset_ref',
    DELETE_REFERENCE = 'delete_reference_ref',
    RUN_AUTO_ANNOTATION = 'run_auto_annotation_ref',
    MODEL_REFERENCE = 'model_reference_ref',
    MOVE_REFERENCE_TO_PROJECT = 'move_reference_to_project_ref',
    OPEN_BUG_TRACKER = 'open_bug_tracker_ref',
    BACKUP_REFERENCE = 'backup_reference_ref',
    VIEW_ANALYTICS = 'view_analytics_ref',
}

function ActionsMenuComponent(props: Props): JSX.Element {
    const {
        referenceID,
        projectID,
        bugTracker,
        inferenceIsActive,
        backupIsActive,
        onClickMenu,
    } = props;

    const plugins = usePlugins((state: CombinedState) => state.plugins.components.referenceActions.items, props);

    const onClickMenuWrapper = useCallback(
        (params: MenuInfo) => {
            if (!params) {
                return;
            }

            if (params.key === Actions.DELETE_REFERENCE) {
                Modal.confirm({
                    title: t('The reference {id} will be deleted', { id: referenceID }),
                    content: t('All related data (images, annotations) will be lost. Continue?'),
                    className: 'cvat-modal-confirm-delete-reference',
                    onOk: () => {
                        onClickMenu(params);
                    },
                    okButtonProps: {
                        type: 'primary',
                        danger: true,
                    },
                    okText: t('Delete'),
                });
            }

            else if (params.key === Actions.MODEL_REFERENCE) {
                Modal.confirm({
                    title: t('Automatic models search for the reference {id}', { id: referenceID }),
                    // content: t('All related data (images, annotations) will be lost. Continue?'),
                    className: 'cvat-modal-confirm-delete-reference',
                    onOk: () => {
                        onClickMenu(params);
                    },
                    okButtonProps: {
                        type: 'primary',
                        // danger: true,
                    },
                    okText: t('Find'),
                });
            }

            else {
                onClickMenu(params);
            }
        },
        [referenceID],
    );

    const menuItems: [JSX.Element, number][] = [];
    menuItems.push([(
        <Menu.Item key={Actions.LOAD_REFERENCE_ANNO}>{t('Upload annotations')}</Menu.Item>
    ), 0]);

    menuItems.push([(
        <Menu.Item key={Actions.EXPORT_REFERENCE_DATASET}>{t('Export reference dataset')}</Menu.Item>
    ), 10]);

    // if (bugTracker) {
    //     menuItems.push([(
    //         <Menu.Item key={Actions.OPEN_BUG_TRACKER}>Open bug tracker</Menu.Item>
    //     ), 20]);
    // }

    menuItems.push([(
        <Menu.Item disabled={inferenceIsActive} key={Actions.RUN_AUTO_ANNOTATION}>
            {t('Automatic annotation')}
        </Menu.Item>
    ), 30]);

    menuItems.push([(
        <React.Fragment key={Actions.MODEL_REFERENCE}>
            <Menu.Item key={Actions.MODEL_REFERENCE} disabled={inferenceIsActive}>
                {t('Automatic 3d model search')}
            </Menu.Item>
        </React.Fragment>
    ), 30]);

    menuItems.push([(
        <Menu.Item
            key={Actions.BACKUP_REFERENCE}
            disabled={backupIsActive}
            icon={backupIsActive && <LoadingOutlined id='cvat-backup-reference-loading' />}
        >
            {t('Backup Reference')}
        </Menu.Item>
    ), 40]);

    // menuItems.push([(
    //     <Menu.Item
    //         key={Actions.VIEW_ANALYTICS}
    //     >
    //         View analytics
    //     </Menu.Item>
    // ), 50]);

    // if (projectID === null) {
    //     menuItems.push([(
    //         <Menu.Item key={Actions.MOVE_REFERENCE_TO_PROJECT}>{t('Move to project')}</Menu.Item>
    //     ), 60]);
    // }

    menuItems.push([(
        <React.Fragment key={Actions.DELETE_REFERENCE}>
            <Menu.Divider />
            <Menu.Item key={Actions.DELETE_REFERENCE}>{t('Delete')}</Menu.Item>
        </React.Fragment>
    ), 70]);

    menuItems.push(
        ...plugins.map(({ component: Component, weight }, index) => {
            const menuItem = Component({ key: index, targetProps: props });
            return [menuItem, weight] as [JSX.Element, number];
        }),
    );

    return (
        <Menu
            selectable={false}
            className='cvat-actions-menu'
            onClick={onClickMenuWrapper}
        >
            { menuItems.sort((menuItem1, menuItem2) => menuItem1[1] - menuItem2[1])
                .map((menuItem) => menuItem[0]) }
        </Menu>
    );
}

export default React.memo(ActionsMenuComponent);
